/** Whitelist for client/issue attachment uploads. */
export const CLIENT_ATTACHMENT_EXTENSIONS = Object.freeze([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "zip",
  "rar",
  "txt",
  "xml",
  "csv",
]);

export const CLIENT_ATTACHMENT_ACCEPTED_TYPES = CLIENT_ATTACHMENT_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",");

const DANGEROUS_EXTENSIONS = new Set([
  "html",
  "htm",
  "xhtml",
  "js",
  "jsx",
  "mjs",
  "cjs",
  "ts",
  "tsx",
  "svg",
  "php",
  "asp",
  "aspx",
  "exe",
  "bat",
  "cmd",
  "com",
  "msi",
  "scr",
  "vbs",
  "ps1",
  "sh",
  "jar",
  "war",
  "hta",
  "shtml",
]);

const EXTENSION_MIME_MAP = Object.freeze({
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xls: ["application/vnd.ms-excel", "application/msexcel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  png: ["image/png"],
  jpg: ["image/jpeg"],
  jpeg: ["image/jpeg"],
  gif: ["image/gif"],
  zip: ["application/zip", "application/x-zip-compressed"],
  rar: ["application/vnd.rar", "application/x-rar-compressed", "application/octet-stream"],
  txt: ["text/plain"],
  xml: ["application/xml", "text/xml"],
});

const ACTIVE_CONTENT_PATTERNS = [
  /<!doctype\s+html/i,
  /<html[\s>]/i,
  /<head[\s>]/i,
  /<body[\s>]/i,
  /<script[\s>]/i,
  /<iframe[\s>]/i,
  /<object[\s>]/i,
  /<embed[\s>]/i,
  /<svg[\s>]/i,
  /javascript\s*:/i,
  /<\?php/i,
  /<%/,
];

const CONTENT_INSPECTION_LIMIT_BYTES = 8192;

/**
 * Parse a comma-separated accept string (e.g. ".pdf,.png,image/*") into extension whitelist.
 * @param {string} acceptedTypes
 * @returns {string[]|null} null when unrestricted ("*")
 */
export function parseAcceptedExtensions(acceptedTypes) {
  if (!acceptedTypes || acceptedTypes === "*") return null;
  const extensions = new Set();
  for (const token of acceptedTypes.split(",")) {
    const trimmed = token.trim().toLowerCase();
    if (!trimmed) continue;
    if (trimmed.startsWith(".")) {
      extensions.add(trimmed.slice(1));
      continue;
    }
    if (trimmed === "image/*") {
      ["png", "jpg", "jpeg", "gif", "webp", "bmp"].forEach((ext) => extensions.add(ext));
    }
  }
  return [...extensions];
}

/**
 * @param {string} filename
 * @returns {string|null}
 */
export function getFileExtension(filename) {
  const base = getSafeBasename(filename);
  if (!base) return null;
  const lastDot = base.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === base.length - 1) return null;
  return base.slice(lastDot + 1).toLowerCase();
}

function getSafeBasename(filename) {
  if (typeof filename !== "string") return "";
  const normalized = filename.replace(/\\/g, "/").trim();
  const base = normalized.split("/").pop() ?? "";
  if (!base || base === "." || base === "..") return "";
  if (base.includes("\0")) return "";
  return base;
}

/**
 * Reject double extensions (e.g. exploit.png.txt) and disguised dangerous types.
 * @param {string} filename
 * @param {Set<string>} allowedExtensions
 * @returns {string|null}
 */
export function validateFilename(filename, allowedExtensions) {
  if (typeof filename !== "string" || /[/\\]/.test(filename) || filename.includes("..")) {
    return "Invalid file name";
  }

  const base = getSafeBasename(filename);
  if (!base) {
    return "Invalid file name";
  }

  const parts = base.split(".").map((part) => part.toLowerCase());
  if (parts.length < 2) {
    return `File "${base}" must include an allowed extension`;
  }

  const extension = parts[parts.length - 1];
  if (!allowedExtensions.has(extension)) {
    return `File type ".${extension}" is not allowed`;
  }

  if (DANGEROUS_EXTENSIONS.has(extension)) {
    return `File type ".${extension}" is not allowed`;
  }

  const middleSegments = parts.slice(1, -1);
  if (middleSegments.length > 0) {
    const suspicious = middleSegments.some(
      (segment) => DANGEROUS_EXTENSIONS.has(segment) || allowedExtensions.has(segment)
    );
    if (suspicious) {
      return `File "${base}" has a disallowed double extension`;
    }
  }

  return null;
}

/**
 * @param {string} extension
 * @param {string} mimeType
 * @returns {string|null}
 */
export function validateMimeType(extension, mimeType) {
  if (!mimeType || mimeType === "application/octet-stream") return null;
  const allowed = EXTENSION_MIME_MAP[extension];
  if (!allowed) return null;
  const normalized = mimeType.toLowerCase().split(";")[0].trim();
  if (allowed.includes(normalized)) return null;
  return `File MIME type "${mimeType}" does not match ".${extension}"`;
}

function decodeBytesToText(bytes) {
  const TextDecoderCtor = globalThis.TextDecoder;
  if (typeof TextDecoderCtor === "function") {
    try {
      return new TextDecoderCtor("utf-8", { fatal: false }).decode(bytes);
    } catch {
      return null;
    }
  }
  let text = "";
  for (let i = 0; i < bytes.length; i++) {
    text += String.fromCharCode(bytes[i]);
  }
  return text;
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {string|null}
 */
export function detectActiveContent(buffer) {
  const bytes = new Uint8Array(buffer);
  const sample = bytes.slice(0, CONTENT_INSPECTION_LIMIT_BYTES);
  const text = decodeBytesToText(sample);
  if (text == null) {
    return null;
  }
  if (ACTIVE_CONTENT_PATTERNS.some((pattern) => pattern.test(text))) {
    return "File contains HTML, JavaScript, or other active content that is not allowed";
  }
  return null;
}

const MAGIC_SIGNATURES = Object.freeze({
  pdf: [[0x25, 0x50, 0x44, 0x46]],
  png: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  jpg: [[0xff, 0xd8, 0xff]],
  jpeg: [[0xff, 0xd8, 0xff]],
  gif: [[0x47, 0x49, 0x46, 0x38]],
  zip: [
    [0x50, 0x4b, 0x03, 0x04],
    [0x50, 0x4b, 0x05, 0x06],
    [0x50, 0x4b, 0x07, 0x08],
  ],
  docx: [[0x50, 0x4b, 0x03, 0x04]],
  xlsx: [[0x50, 0x4b, 0x03, 0x04]],
  rar: [[0x52, 0x61, 0x72, 0x21, 0x1a, 0x07]],
});

function matchesMagicSignature(bytes, signatures) {
  return signatures.some((signature) => signature.every((value, index) => bytes[index] === value));
}

/**
 * @param {string} extension
 * @param {ArrayBuffer} buffer
 * @returns {string|null}
 */
export function validateBinarySignature(extension, buffer) {
  const bytes = new Uint8Array(buffer.slice(0, 16));
  const signatures = MAGIC_SIGNATURES[extension];
  if (!signatures) return null;
  if (matchesMagicSignature(bytes, signatures)) return null;
  return `File content does not match the ".${extension}" file type`;
}

async function readFilePrefix(file, maxBytes = CONTENT_INSPECTION_LIMIT_BYTES) {
  const slice = file.slice(0, maxBytes);
  if (typeof slice.arrayBuffer === "function") {
    return slice.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsArrayBuffer(slice);
  });
}

/**
 * Validate an upload against extension whitelist, MIME type, and file content.
 * @param {File} file
 * @param {{ allowedExtensions?: string[], inspectContent?: boolean }} [options]
 * @returns {Promise<string|null>} error message or null when valid
 */
export async function validateUploadFile(file, options = {}) {
  const allowedList = options.allowedExtensions ?? CLIENT_ATTACHMENT_EXTENSIONS;
  const allowedExtensions = new Set(allowedList.map((ext) => ext.toLowerCase()));
  const inspectContent = options.inspectContent !== false;
  const name = file?.name ?? "";

  const filenameError = validateFilename(name, allowedExtensions);
  if (filenameError) return filenameError;

  const extension = getFileExtension(name);
  if (!extension) {
    return `File "${name}" must include an allowed extension`;
  }

  const mimeError = validateMimeType(extension, file.type ?? "");
  if (mimeError) return mimeError;

  if (!inspectContent) return null;

  const buffer = await readFilePrefix(file);
  const signatureError = validateBinarySignature(extension, buffer);
  if (signatureError) return signatureError;

  if (extension === "txt" || extension === "pdf" || extension === "xml") {
    const activeContentError = detectActiveContent(buffer);
    if (activeContentError) return activeContentError;
  }

  return null;
}

/**
 * @param {File[]} files
 * @param {{ allowedExtensions?: string[] }} [options]
 * @returns {Promise<string|null>}
 */
export async function validateUploadFiles(files, options = {}) {
  for (const file of files ?? []) {
    const error = await validateUploadFile(file, options);
    if (error) return error;
  }
  return null;
}
